import React, { useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";
import { brl } from "@/api";
import { toast } from "sonner";

// Termo de Responsabilidade e Procedência do Produto recebido em troca.
// Impressão: abre uma janela dedicada com o HTML autônomo do termo (evita
// conflito com o `position: fixed` do Dialog Radix, que causava PDF em branco).
export default function TradeInTerm({ tradeIn, customer, store, onClose }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("pt-BR");
  const timeStr = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const clientName = customer?.name || "—";
  const clientCpf = customer?.cpf || "—";
  const clientPhone = customer?.phone || "";
  const clientAddress = customer?.address || "";
  const storeName = store?.name || "";
  const storeCnpj = store?.cnpj || "";
  const containerRef = useRef(null);

  const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
  ));

  const buildPrintHtml = () => {
    const c = escapeHtml;
    const value = brl(Number(tradeIn?.value || 0));
    return `<!doctype html>
<html lang="pt-BR"><head>
<meta charset="utf-8"/>
<title>Termo de Troca — ${c(clientName)}</title>
<style>
  @page { size: A4; margin: 8mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000;
    font-family: Georgia, 'Times New Roman', serif; font-size: 9pt; line-height: 1.25; }
  .wrap { padding: 2mm 4mm; max-width: 195mm; margin: 0 auto; }
  .center { text-align: center; }
  .right { text-align: right; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3mm; }
  .grid2sig { display: grid; grid-template-columns: 1fr 1fr; gap: 10mm; margin-top: 8mm; }
  h1.head { font-size: 10pt; border-top: 1px solid #000; border-bottom: 1px solid #000;
    padding: 3px 0; margin: 5px 0; text-transform: uppercase; letter-spacing: .4px; }
  h2.store { font-size: 11pt; margin: 0 0 1mm; text-transform: uppercase; letter-spacing: .3px; }
  .box { border: 1px solid #000; border-radius: 3px; padding: 3px 6px; margin: 2px 0 4px; }
  .box div { margin: 0; }
  .label { font-size: 7.5pt; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700; margin: 4px 0 1px; }
  .termo { margin-top: 3mm; line-height: 1.3; text-align: justify; }
  .termo p { margin: 2px 0; }
  .termo .title { text-align: center; font-weight: 700; text-transform: uppercase; margin: 1mm 0 2mm; font-size: 9.5pt; }
  .sig { text-align: center; }
  .sig .line { border-top: 1px solid #000; padding-top: 3px; font-size: 8.5pt; }
  .sig .name { font-weight: 700; }
  .footer { margin-top: 4mm; padding-top: 3px; border-top: 1px solid #000;
    text-align: center; font-size: 8.5pt; }
  .no-break { page-break-inside: avoid; break-inside: avoid; }
</style>
</head><body>
<div class="wrap">
  ${storeName ? `<div class="center"><h2 class="store">${c(storeName)}</h2>${storeCnpj ? `<div style="font-size:8.5pt;">CNPJ: ${c(storeCnpj)}</div>` : ""}</div>` : ""}
  <h1 class="head center">Recibo de Troca / Termo de Responsabilidade</h1>
  <div class="grid2" style="font-size:9pt; margin: 1mm 0 2mm;">
    <div><strong>Data:</strong> ${c(dateStr)}</div>
    <div class="right"><strong>Hora:</strong> ${c(timeStr)}</div>
  </div>

  <div class="label">Cliente que entrega o produto</div>
  <div class="box">
    <div><strong>Nome:</strong> ${c(clientName)}</div>
    <div><strong>CPF:</strong> ${c(clientCpf)}</div>
    ${clientPhone ? `<div><strong>Telefone:</strong> ${c(clientPhone)}</div>` : ""}
    ${clientAddress ? `<div><strong>Endereço:</strong> ${c(clientAddress)}</div>` : ""}
  </div>

  <div class="label">Produto recebido em troca</div>
  <div class="box">
    <div><strong>Produto:</strong> ${c(tradeIn?.product_name || "—")}</div>
    ${tradeIn?.brand ? `<div><strong>Marca / Modelo:</strong> ${c(tradeIn.brand)}</div>` : ""}
    ${tradeIn?.code ? `<div><strong>Código / IMEI:</strong> ${c(tradeIn.code)}</div>` : ""}
    ${tradeIn?.condition ? `<div><strong>Estado / Observações:</strong> ${c(tradeIn.condition)}</div>` : ""}
    ${tradeIn?.notes ? `<div><strong>OBS adicionais:</strong> ${c(tradeIn.notes)}</div>` : ""}
    <div><strong>Valor abatido:</strong> ${c(value)}</div>
  </div>

  <div class="termo">
    <div class="title">Termo de Responsabilidade e Procedência do Produto</div>
    <p>Ao assinar este termo, o cliente que vendeu ou entregou o produto acima identificado como parte de pagamento/troca declara, para todos os fins, ser legítimo proprietário ou possuidor do referido produto, responsabilizando-se integralmente pela sua procedência e origem.</p>
    <p>O cliente declara que adquiriu e utilizou o produto de forma lícita, comprometendo-se a informar à empresa qualquer fato ou circunstância que possa afetar sua propriedade, utilização ou regularidade.</p>
    <p>Na data da presente negociação, o IMEI informado para o produto foi consultado e, conforme a consulta realizada junto à ANATEL, não apresentava restrições de uso ou bloqueios registrados naquele momento.</p>
    <p>Caso seja posteriormente constatada qualquer irregularidade relacionada à procedência ou propriedade do produto, incluindo, mas não se limitando a, bloqueio ou restrição de IMEI, registro de roubo ou furto, impedimento de uso ou qualquer outra irregularidade decorrente de sua origem ou aquisição, o cliente <strong>${c(clientName)}</strong>, inscrito no CPF sob nº <strong>${c(clientCpf)}</strong>, declara-se responsável pelas consequências decorrentes da informação prestada neste termo, sem prejuízo das demais responsabilidades previstas na legislação aplicável.</p>
    <p>Ao assinar, o cliente declara estar ciente e de acordo com as condições acima.</p>
  </div>

  <div class="grid2sig no-break">
    <div class="sig"><div class="line"><div class="name">${c(clientName)}</div><div>Cliente (entregou o produto)</div></div></div>
    <div class="sig"><div class="line"><div class="name">${c(storeName || "Proprietário / Responsável")}</div><div>Proprietário / Responsável da Loja</div></div></div>
  </div>

  ${(storeName || storeCnpj) ? `<div class="footer no-break">${storeName ? `<div><strong>${c(storeName)}</strong></div>` : ""}${storeCnpj ? `<div>CNPJ: ${c(storeCnpj)}</div>` : ""}</div>` : ""}
</div>
<script>
  window.onload = function () {
    // Pequeno delay pra layout estabilizar antes de imprimir
    setTimeout(function () { window.focus(); window.print(); }, 150);
  };
  // Fecha a janela quando o print for finalizado/cancelado
  window.onafterprint = function () { window.close(); };
</script>
</body></html>`;
  };

  const handlePrint = () => {
    try {
      const html = buildPrintHtml();
      const w = window.open("", "_blank", "width=820,height=920,menubar=no,toolbar=no,location=no,status=no");
      if (!w) {
        // Popup bloqueado — fallback para blob URL + toast informativo
        const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        const w2 = window.open(url, "_blank");
        if (!w2) toast.error("Popups bloqueados pelo navegador. Habilite popups para imprimir o termo.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      // fallback silencioso
      window.print();
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto print-container"
        data-testid="trade-in-term-dialog"
      >
        <DialogTitle className="sr-only">Termo de Troca</DialogTitle>
        <DialogDescription className="sr-only">Prévia do termo de responsabilidade e procedência do produto recebido na troca. Use o botão Imprimir para gerar o documento em nova janela.</DialogDescription>

        {/* Toolbar */}
        <div className="flex items-center justify-between border-b pb-3">
          <div className="text-sm font-semibold">Termo de Troca</div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handlePrint} data-testid="print-trade-term-btn">
              <Printer size={14} className="mr-1.5"/> Imprimir
            </Button>
            <Button size="sm" variant="outline" onClick={onClose} data-testid="close-trade-term-btn">
              <X size={14}/>
            </Button>
          </div>
        </div>

        {/* Documento */}
        <div id="trade-in-term-print" ref={containerRef} className="text-neutral-900 dark:text-neutral-100 print:text-black">
          {/* Cabeçalho da loja */}
          <div className="text-center mb-4">
            <div className="text-lg font-bold uppercase tracking-wide">{storeName || "\u00A0"}</div>
            {storeCnpj && <div className="text-xs">CNPJ: {storeCnpj}</div>}
          </div>

          <h1 className="text-center text-base font-bold uppercase mt-2 mb-4 border-y py-2">
            Recibo de Troca / Termo de Responsabilidade
          </h1>

          <div className="grid grid-cols-2 gap-x-4 text-sm mb-3">
            <div><strong>Data:</strong> {dateStr}</div>
            <div className="text-right"><strong>Hora:</strong> {timeStr}</div>
          </div>

          {/* Dados do cliente */}
          <div className="mb-4 text-sm">
            <div className="text-xs uppercase tracking-widest font-semibold mb-1">Cliente que entrega o produto</div>
            <div className="border rounded p-2 space-y-0.5">
              <div><strong>Nome:</strong> {clientName}</div>
              <div><strong>CPF:</strong> {clientCpf}</div>
              {clientPhone && <div><strong>Telefone:</strong> {clientPhone}</div>}
              {clientAddress && <div><strong>Endereço:</strong> {clientAddress}</div>}
            </div>
          </div>

          {/* Dados do produto na troca */}
          <div className="mb-4 text-sm">
            <div className="text-xs uppercase tracking-widest font-semibold mb-1">Produto recebido em troca</div>
            <div className="border rounded p-2 space-y-0.5">
              <div><strong>Produto:</strong> {tradeIn?.product_name || "—"}</div>
              {tradeIn?.brand && <div><strong>Marca / Modelo:</strong> {tradeIn.brand}</div>}
              {tradeIn?.code && <div><strong>Código / IMEI:</strong> {tradeIn.code}</div>}
              {tradeIn?.condition && <div><strong>Estado / Observações:</strong> {tradeIn.condition}</div>}
              {tradeIn?.notes && <div><strong>OBS adicionais:</strong> {tradeIn.notes}</div>}
              <div><strong>Valor abatido:</strong> {brl(Number(tradeIn?.value || 0))}</div>
            </div>
          </div>

          {/* Termo de segurança */}
          <div className="text-[13px] leading-relaxed mt-5 space-y-2">
            <div className="font-bold text-center uppercase">Termo de Responsabilidade e Procedência do Produto</div>
            <p>
              Ao assinar este termo, o cliente que vendeu ou entregou o produto acima identificado como parte de pagamento/troca declara, para todos os fins, ser legítimo proprietário ou possuidor do referido produto, responsabilizando-se integralmente pela sua procedência e origem.
            </p>
            <p>
              O cliente declara que adquiriu e utilizou o produto de forma lícita, comprometendo-se a informar à empresa qualquer fato ou circunstância que possa afetar sua propriedade, utilização ou regularidade.
            </p>
            <p>
              Na data da presente negociação, o IMEI informado para o produto foi consultado e, conforme a consulta realizada junto à ANATEL, não apresentava restrições de uso ou bloqueios registrados naquele momento.
            </p>
            <p>
              Caso seja posteriormente constatada qualquer irregularidade relacionada à procedência ou propriedade do produto, incluindo, mas não se limitando a, bloqueio ou restrição de IMEI, registro de roubo ou furto, impedimento de uso ou qualquer outra irregularidade decorrente de sua origem ou aquisição, o cliente <strong>{clientName}</strong>, inscrito no CPF sob nº <strong>{clientCpf}</strong>, declara-se responsável pelas consequências decorrentes da informação prestada neste termo, sem prejuízo das demais responsabilidades previstas na legislação aplicável.
            </p>
            <p>Ao assinar, o cliente declara estar ciente e de acordo com as condições acima.</p>
          </div>

          {/* Assinaturas */}
          <div className="grid grid-cols-2 gap-8 mt-10">
            <div className="text-center">
              <div className="border-t border-black pt-1 text-xs">
                <div className="font-semibold">{clientName}</div>
                <div>Cliente (entregou o produto)</div>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-black pt-1 text-xs">
                <div className="font-semibold">{storeName || "Proprietário / Responsável"}</div>
                <div>Proprietário / Responsável da Loja</div>
              </div>
            </div>
          </div>

          {/* Rodapé da loja */}
          <div className="mt-8 pt-3 border-t text-center text-xs text-neutral-600 print:text-black">
            {storeName && <div><strong>{storeName}</strong></div>}
            {storeCnpj && <div>CNPJ: {storeCnpj}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
